import { memo, useCallback, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import DateRangeCalendar from "../DateRangeCalendar/DateRangeCalendar";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { _, _any, Locale } from "i18n";
import "./seasonDateRangeSelector.scss";

export interface SeasonOption {
	label: string;
	value: number | null;
}

interface SeasonDateRangeSelectorProps {
	seasons: SeasonOption[];
	selectedSeason?: number | null;
	onSeasonChange: (season: number | null) => void;
	dateRangeStart?: string;
	dateRangeEnd?: string;
	onDateRangeChange: (start: string, end: string) => void;
	locale: Locale;
	placeholder?: string;
	label?: string;
}

function SeasonDateRangeSelectorComponent({
	seasons,
	selectedSeason,
	onSeasonChange,
	dateRangeStart = "",
	dateRangeEnd = "",
	onDateRangeChange,
	locale,
	placeholder,
	label,
}: SeasonDateRangeSelectorProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [tempSeason, setTempSeason] = useState<number | null>(selectedSeason ?? null);
	const [tempDateStart, setTempDateStart] = useState(dateRangeStart);
	const [tempDateEnd, setTempDateEnd] = useState(dateRangeEnd);

	useOnClickOutside(containerRef, () => {
		setIsOpen(false);
		setTempSeason(selectedSeason ?? null);
		setTempDateStart(dateRangeStart);
		setTempDateEnd(dateRangeEnd);
	});

	const getDisplayValue = (): string => {
		if (dateRangeStart && dateRangeEnd) {
			return `${dateRangeStart} - ${dateRangeEnd}`;
		}
		if (selectedSeason) {
			return seasons.find((season) => season.value === selectedSeason)?.label ?? "";
		}
		return placeholder || _("seasonSelector.placeholder");
	};

	const handleSeasonClick = (year: number) => {
		if (tempSeason === year) {
			setTempSeason(null);
		} else {
			setTempSeason(year);
			setTempDateStart("");
			setTempDateEnd("");
		}
	};

	const handleDateRangeChange = useCallback((start: string, end: string) => {
		setTempDateStart(start);
		setTempDateEnd(end);
		if (start && end) {
			setTempSeason(null);
		}
	}, []);

	const handleApply = () => {
		if (tempDateStart && tempDateEnd) {
			onDateRangeChange(tempDateStart, tempDateEnd);
		} else if (tempSeason) {
			onSeasonChange(tempSeason);
		} else {
			onSeasonChange(null);
		}
		setIsOpen(false);
	};

	const handleCancel = () => {
		setTempSeason(selectedSeason ?? null);
		setTempDateStart(dateRangeStart);
		setTempDateEnd(dateRangeEnd);
		setIsOpen(false);
	};

	const isDateRangeSelected = Boolean(tempDateStart && tempDateEnd);
	const isSeasonSelected = Boolean(tempSeason);
	const hasValue = Boolean(selectedSeason || (dateRangeStart && dateRangeEnd));

	const handleToggle = () => {
		if (!isOpen) {
			setTempSeason(selectedSeason ?? null);
			setTempDateStart(dateRangeStart);
			setTempDateEnd(dateRangeEnd);
		}
		setIsOpen(!isOpen);
	};

	return (
		<div className="season-date-selector" ref={containerRef}>
			{label && <span className="season-date-selector-label"><CalendarIcon size={16} />{label}</span>}
			<button
				type="button"
				className={`season-date-selector-trigger ${hasValue ? "has-value" : ""}`}
				onClick={handleToggle}
			>
				<span className="season-date-selector-trigger-text">{getDisplayValue()}</span>
				<ChevronDown size={16} />
			</button>

			{isOpen && (
				<div className="season-date-selector-dropdown">
					<div className="season-date-selector-section">
						<h4 className="season-date-selector-section-title">
							{_("seasonSelector.selectSeason")}
						</h4>
						<div className={`season-date-selector-seasons ${isDateRangeSelected ? "disabled" : ""}`}>
							{seasons.map((season) => {
								if (season.value == null) {
									return null;
								}

								const isSelected = tempSeason === season.value;
								return (
									<button
										key={season.value}
										type="button"
										className={`season-date-selector-season-item ${isSelected ? "selected" : ""}`}
										onClick={() => handleSeasonClick(season.value as number)}
										disabled={isDateRangeSelected}
									>
										<div className={`season-date-selector-checkbox ${isSelected ? "checked" : ""}`}>
											{isSelected && <span className="icon-new icon-check icon-12" />}
										</div>
										<div className="season-date-selector-season-label">
											<span className="season-short">{season.label}</span>
										</div>
									</button>
								);
							})}
						</div>
					</div>

					<div className="season-date-selector-divider" />

					<div className="season-date-selector-section">
						<h4 className="season-date-selector-section-title">
							{_("seasonSelector.orSelectTimePeriod")}
						</h4>
						<div className={`season-date-selector-daterange ${isSeasonSelected ? "disabled" : ""}`}>
							<DateRangeCalendar
								locale={locale}
								start={tempDateStart}
								end={tempDateEnd}
								onChange={handleDateRangeChange}
								placeholder={_("seasonSelector.selectDateRange")}
								apiFormat={true}
							/>
						</div>
					</div>

					<div className="season-date-selector-footer">
						<button
							type="button"
							className="season-date-selector-btn-cancel"
							onClick={handleCancel}
						>
							{_("common.action.cancel")}
						</button>
						<button
							type="button"
							className="season-date-selector-btn-apply"
							onClick={handleApply}
						>
							{_("common.action.confirm")}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export const SeasonDateRangeSelector = memo(SeasonDateRangeSelectorComponent);
export default SeasonDateRangeSelector;
